import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/authContext';
import { generateRSAKeyPair, exportPublicKey, exportPrivateKey, decryptPrivateKeyWithPassword } from '../../../utils/cryptoUtils';
import { ethers } from 'ethers'; // Test
import { makeRequest } from '../../../utils/axios'; 

export const useAuthLogin = () => {
  const [inputs, setInputs] = useState({
    username: "",
    password: "",
  });
  
  const [err, setErr] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  
  const { login, loginWeb3, setPublicKey, setPrivateKey, setCurrentUser } = useContext(AuthContext);

  const handleChange = (e) => {
    setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault(); 
    setIsLoggingIn(true);
    setErr(null); 

    try {
      const userData = await login(inputs);

      if (!userData?.publicKey || !userData?.encryptedPrivateKey) {
          throw new Error("This account has not been configured with an E2EE security key. Please register again.");
      }

      let decryptedPrivateKey;
      try {
          decryptedPrivateKey = await decryptPrivateKeyWithPassword(userData.encryptedPrivateKey, inputs.password);
      } catch (cryptoErr) {
          throw new Error("Incorrect decryption password or corrupted security key!");
      }

      const finalPublicKey = userData.publicKey;

      setCurrentUser(userData);
      setPrivateKey(decryptedPrivateKey);
      setPublicKey(finalPublicKey);

      // localStorage.setItem("myPrivateKey", decryptedPrivateKey);
      localStorage.setItem("myPublicKey", finalPublicKey);
      // localStorage.setItem(`privateKey_${inputs.username}`, decryptedPrivateKey);
      localStorage.setItem(`publicKey_${inputs.username}`, finalPublicKey);

      navigate("/");

    } 
    catch (err) {
      const errorMessage = err.response?.data?.error 
                        || err.response?.data?.message 
                        || (typeof err.response?.data === 'string' ? err.response?.data : "Something went wrong!");
      setErr(errorMessage);
    }
    finally {
      setIsLoggingIn(false);
    }
  };

  const handleWeb3Login = async () => {
    setIsLoggingIn(true);
    setErr(null);

    try {
      if (!window.ethereum) {
        throw new Error("Please install MetaMask to use Web3 Login!");
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const walletAddress = accounts[0];

      const nonceRes = await makeRequest.get(`/auth/web3/nonce?wallet_address=${walletAddress}`);
      const signMessage = nonceRes.data.message;

      const signer = await provider.getSigner();
      const signature = await signer.signMessage(signMessage);

      const userData = await loginWeb3({
        wallet_address: walletAddress,
        signature: signature,
      });
      
      navigate("/");

    } catch (error) {
        console.error(error);
        if (error.code === 4001) {
            setErr("You rejected the signature request in MetaMask.");
        } else {
            const errorMessage = error.response?.data || error.message || "Web3 Login Failed!";
            setErr(errorMessage);
        }
    } finally {
        setIsLoggingIn(false);
    }
  };

  return {
    inputs,
    err,
    isLoggingIn,
    handleChange,
    handleLogin,
    handleWeb3Login 
  };
};